import { setLogin, setUser, setUsers, setViewingUser } from "../state";

const BASE_URL = process.env.REACT_APP_BASE_URL;

// Login user
export const loginUser = (values) => async (dispatch) => {
  try {
    const loggedInResponse = await fetch(`${BASE_URL}/auth/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(values),
    });
    const loggedIn = await loggedInResponse.json();

    if (loggedIn) {
      dispatch(
        setLogin({
          user: loggedIn.user,
          token: loggedIn.token,
        })
      );
    }

    return loggedIn;
  } catch (error) {
    console.error("Error while logging in:", error);
    return null;
  }
};

// Fetch a single user by ID
export const fetchUser =
  (id, viewingUser = false) =>
  async (dispatch, getState) => {
    try {
      const token = getState().token;

      const userResponse = await fetch(`${BASE_URL}/users/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const user = await userResponse.json();

      if (!viewingUser) {
        dispatch(setUser(user));
      } else {
        dispatch(setViewingUser(user));
      }

      return user;
    } catch (error) {
      console.error("Error while fetching user:", error);
      return null;
    }
  };

// Fetch all users with optional filters
export const fetchUsers =
  ({
    page = 1,
    pageSize = 10,
    sort = "createdAt",
    order = "desc",
    search,
    location,
    filters,
  } = {}) =>
  async (dispatch, getState) => {
    try {
      const token = getState().token;

      const query = new URLSearchParams({
        page: Number(page),
        limit: Number(pageSize),
        sort,
        order,
        search,
        location,
        ...(filters && { filters: JSON.stringify(filters) }),
      }).toString();

      const usersResponse = await fetch(`${BASE_URL}/users?${query}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!usersResponse.ok) throw new Error("Failed to fetch users");

      const data = await usersResponse.json();

      dispatch(setUsers({ data: data.users, total: data.total }));

      return data;
    } catch (error) {
      console.error("Error while fetching users:", error);
      return null;
    }
  };

// Create a new user
export const createUser = (values) => async (dispatch, getState) => {
  try {
    const token = getState().token;

    const response = await fetch(`${BASE_URL}/auth/register`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(values),
    });

    const newUser = await response.json();

    if (!response.ok || !newUser) {
      throw new Error(
        newUser?.message || newUser?.error || "Failed to create user"
      );
    }

    return {
      success: !!newUser.success,
      message: newUser.message || "User created successfully",
      user: newUser.user || newUser,
      token: newUser.token,
    };
  } catch (error) {
    console.error("Error while creating user:", error);
    return {
      success: false,
      message: error.message || "Failed to create user",
    };
  }
};

// Update an existing user
export const updateUser = (id, values) => async (dispatch, getState) => {
  try {
    const token = getState().token;

    const response = await fetch(`${BASE_URL}/users/${id}`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(values),
    });

    const updatedUser = await response.json();

    if (!response.ok || !updatedUser) throw new Error(updatedUser.message);

    return { success: true, message: updatedUser.message };
  } catch (error) {
    console.error("Error while updating user:", error);
    return {
      error: true,
      success: false,
      message: error.message,
    };
  }
};

// Delete a user
export const deleteUser = (id) => async (dispatch, getState) => {
  try {
    const token = getState().token;

    const response = await fetch(`${BASE_URL}/users/${id}`, {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    const responseData = await response.json();

    if (!response.ok || !responseData) throw new Error(responseData.message);

    // Refetch users after deleting
    dispatch(
      fetchUsers({
        page: 1,
        limit: 10,
        sort: "createdAt",
        search: "",
        location: "",
      })
    );
    return { success: true, message: responseData.message };
  } catch (error) {
    console.error("Error while deleting user:", error);
    return {
      error: true,
      success: false,
      message: error.message,
    };
  }
};

export const searchUsers = (searchTerm) => async (dispatch, getState) => {
  try {
    const token = getState().token;

    const response = await fetch(
      `${BASE_URL}/users/search?search=${searchTerm}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    const users = await response.json();

    if (!response.ok || !users) throw new Error(users.message);

    dispatch(setUsers({ data: users, total: users.length }));

    return users;
  } catch (error) {
    console.error("Error while searching users:", error);
    return null;
  }
};
